import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FacebookLogo, TiktokLogo, CheckCircle, WarningCircle, Copy, CaretDown, LinkBreak } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { PasswordInput } from "./PasswordInput";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const inputCls = "h-11 bg-transparent border-white/10 text-white placeholder:text-white/25 focus:border-white/40";

export const SOCIAL_PROVIDERS = {
  meta: {
    title: "Meta (Facebook / Instagram)",
    icon: FacebookLogo,
    color: "text-sky-400",
    border: "hover:border-sky-400/50",
    connectLabel: "Accedi con Facebook",
    connectClass: "bg-[#1877F2] hover:bg-[#3b8bf5] text-white",
    desc: "Collega AREAPAQ a Meta Business: campagne pubblicitarie su Facebook e Instagram, account e pagine.",
    idLabel: "App ID", idPlaceholder: "Es. 1234567890123456",
    guide: [
      { title: "1. Crea l'app Meta", steps: [
        "Vai su developers.facebook.com/apps e clicca \"Crea app\" (tipo Business), collegandola al tuo Business Manager",
        "In \"Casi d'uso\" aggiungi \"Autenticazione e richiesta dati\" / Accesso con Facebook (Web)",
      ]},
      { title: "2. Configura l'accesso", steps: [
        "In \"Accesso con Facebook → Impostazioni\" incolla l'URI di reindirizzamento mostrato qui sopra (aggiungi anche quello di produzione www.areapaq.it)",
        "In \"Impostazioni → Di base\" copia App ID e App Secret nei campi qui sopra e salva",
      ]},
      { title: "3. Permessi e revisione", steps: [
        "In modalità Sviluppo il collegamento funziona solo con il tuo account (e i ruoli app aggiunti)",
        "Per usarla con altri utenti servono App Review e verifica del Business (permessi: ads_read, ads_management, pages_show_list, business_management, instagram_basic)",
      ]},
      { title: "4. Collega", steps: [
        "Salva le credenziali, clicca \"Accedi con Facebook\" e autorizza con l'account che gestisce le inserzioni",
        "Poi in Marketing → Social seleziona l'account pubblicitario da gestire",
      ]},
    ],
  },
  tiktok: {
    title: "TikTok Ads",
    icon: TiktokLogo,
    color: "text-fuchsia-400",
    border: "hover:border-fuchsia-400/50",
    connectLabel: "Collega TikTok",
    connectClass: "bg-fuchsia-600 hover:bg-fuchsia-500 text-white",
    desc: "Collega AREAPAQ a TikTok for Business: campagne pubblicitarie e reportistica degli account inserzionista.",
    idLabel: "App ID", idPlaceholder: "App ID dell'app TikTok Business",
    guide: [
      { title: "1. Account sviluppatore", steps: [
        "Vai su business-api.tiktok.com (TikTok for Business — Developers) e accedi con l'account TikTok Business",
        "Crea un'organizzazione se richiesto",
      ]},
      { title: "2. Crea l'app", steps: [
        "My Apps → \"Create an app\": scegli Marketing API e richiedi i permessi di lettura/gestione Ads e Reporting",
        "Nel campo \"Advertiser redirect URL\" incolla l'URI di reindirizzamento mostrato qui sopra (aggiungi anche quello di produzione)",
        "Invia l'app per l'approvazione di TikTok (1-2 giorni)",
      ]},
      { title: "3. Credenziali", steps: [
        "Approvata l'app, copia App ID e Secret dalla pagina dell'app nei campi qui sopra e salva",
      ]},
      { title: "4. Collega", steps: [
        "Clicca \"Collega TikTok\" e autorizza gli account inserzionista da gestire",
        "Poi in Marketing → Social seleziona l'account inserzionista attivo",
      ]},
    ],
  },
};

export const SocialConfigDialog = ({ provider, onClose, cfg, onSaved }) => {
  const meta = provider ? SOCIAL_PROVIDERS[provider] : null;
  const [form, setForm] = useState({ app_id: "", app_secret: "" });
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);

  useEffect(() => {
    if (provider) {
      setForm({ app_id: cfg?.app_id || "", app_secret: "" });
      setGuideOpen(false);
    }
  }, [provider, cfg]);

  if (!meta) return null;
  const Icon = meta.icon;

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/social/${provider}/credentials`, form);
      toast.success(data.message);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.get(`/admin/social/${provider}/oauth/start`);
      window.location.assign(data.authorization_url);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      const { data } = await api.post(`/admin/social/${provider}/disconnect`);
      toast.success(data.message);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setDisconnecting(false);
    }
  };

  const copyRedirect = () => navigator.clipboard.writeText(cfg?.redirect_uri || "").then(() => toast.success("URI copiato"));

  return (
    <Dialog open={!!provider} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-surface border-white/10 text-white sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid={`social-${provider}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight text-xl flex items-center gap-2">
            <Icon size={20} weight="duotone" className={meta.color} /> {meta.title}
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-sm">{meta.desc} Le credenziali vengono cifrate nel database.</DialogDescription>
        </DialogHeader>

        {cfg?.connected ? (
          <p className="text-xs font-semibold text-emerald-400 flex items-center gap-1.5" data-testid={`social-${provider}-status`}>
            <CheckCircle size={14} weight="fill" /> Account collegato{cfg.active_account_name ? ` — ${cfg.active_account_name}` : ""}
          </p>
        ) : (
          <p className="text-xs font-semibold text-amber-400 flex items-center gap-1.5" data-testid={`social-${provider}-status`}>
            <WarningCircle size={14} weight="fill" /> {cfg?.has_credentials ? "Credenziali salvate — manca l'autorizzazione" : "Non configurato"}
          </p>
        )}

        <form onSubmit={handleSave} className="space-y-4 mt-1">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.2em] uppercase text-white/60">{meta.idLabel}</Label>
              <Input required data-testid={`social-${provider}-appid-input`} value={form.app_id}
                onChange={(e) => setForm({ ...form, app_id: e.target.value })} placeholder={meta.idPlaceholder} className={inputCls} />
            </div>
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.2em] uppercase text-white/60">App Secret {cfg?.has_credentials && <span className="text-white/30 normal-case tracking-normal">(salvato)</span>}</Label>
              <PasswordInput data-testid={`social-${provider}-secret-input`} value={form.app_secret}
                onChange={(e) => setForm({ ...form, app_secret: e.target.value })} placeholder={cfg?.has_credentials ? "••••••••" : ""} className={inputCls} />
            </div>
          </div>
          <div className="p-3 rounded-lg bg-white/[0.03] border border-white/10">
            <p className="text-[10px] tracking-[0.2em] uppercase text-white/40 mb-1">URI di reindirizzamento (da registrare nell'app)</p>
            <div className="flex items-center gap-2">
              <code className="text-[11px] text-sky-300 break-all flex-1" data-testid={`social-${provider}-redirect-uri`}>{cfg?.redirect_uri || "..."}</code>
              <Button type="button" onClick={copyRedirect} variant="ghost" data-testid={`social-${provider}-copy-redirect`}
                className="h-8 w-8 p-0 shrink-0 border border-white/15 text-white/60 hover:text-white hover:bg-white/5 rounded-full">
                <Copy size={13} />
              </Button>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 pt-1">
            <Button type="submit" disabled={saving} data-testid={`social-${provider}-save-button`}
              className="h-11 px-6 bg-white text-black hover:bg-gray-200 rounded-full font-semibold text-sm">
              {saving ? "Salvataggio..." : "Salva credenziali"}
            </Button>
            <Button type="button" onClick={handleConnect} disabled={connecting || !cfg?.has_credentials}
              data-testid={`social-${provider}-connect-button`}
              className={`h-11 px-6 rounded-full font-semibold text-sm disabled:opacity-40 ${meta.connectClass}`}>
              <Icon size={16} weight="bold" className="mr-1.5" /> {connecting ? "Reindirizzamento..." : meta.connectLabel}
            </Button>
            {cfg?.connected && (
              <Button type="button" onClick={handleDisconnect} disabled={disconnecting} data-testid={`social-${provider}-disconnect-button`} variant="ghost"
                className="h-11 px-5 border border-red-500/40 text-red-400 hover:bg-red-500/10 hover:text-red-300 rounded-full font-semibold text-sm">
                <LinkBreak size={15} className="mr-1.5" /> {disconnecting ? "..." : "Disconnetti"}
              </Button>
            )}
          </div>
        </form>

        <div className="mt-2 rounded-lg border border-white/10 overflow-hidden">
          <button type="button" data-testid={`social-${provider}-guide-toggle`} onClick={() => setGuideOpen((v) => !v)}
            className="w-full flex items-center gap-2 p-3 text-left hover:bg-white/[0.03] transition-colors">
            <span className="text-xs font-semibold text-white/70 flex-1">Guida: come ottenere le credenziali</span>
            <CaretDown size={14} weight="bold" className={`text-white/40 transition-transform duration-200 ${guideOpen ? "rotate-180" : ""}`} />
          </button>
          {guideOpen && (
            <div className="px-4 pb-4 pt-1 border-t border-white/5 space-y-4" data-testid={`social-${provider}-guide-content`}>
              {meta.guide.map((sec) => (
                <div key={sec.title}>
                  <p className="text-xs font-semibold text-white/80 mb-1.5">{sec.title}</p>
                  <ul className="space-y-1">
                    {sec.steps.map((s, i) => (
                      <li key={i} className="text-[11px] text-slate-400 leading-relaxed pl-3 relative before:content-[''] before:absolute before:left-0 before:top-[7px] before:w-1 before:h-1 before:rounded-full before:bg-white/30">{s}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
